import { Skeleton } from "@/components/ui/skeleton";
import { useI18n } from "@/locales/i18n-provider";
import { cn } from "@/utils";

const ACTIVITY_ROWS = ["w-40", "w-52", "w-32", "w-48"] as const;
const PROPERTY_ROWS = [
  { id: "team", label: "w-14", value: "w-28" },
  { id: "status", label: "w-10", value: "h-10 w-full" },
  { id: "priority", label: "w-12", value: "h-10 w-full" },
  { id: "project", label: "w-10", value: "w-24" },
  { id: "category", label: "w-10", value: "h-10 w-full" },
] as const;

/** 头部操作在工单返回前不可用，用等尺寸骨架避免出现可点击错觉。 */
export function TicketDetailActionsSkeleton({ showStatusAction }: { showStatusAction: boolean }) {
  return (
    <div aria-hidden="true" className="flex items-center gap-1.5">
      {showStatusAction ? <Skeleton className="h-9 w-9 sm:w-24" /> : null}
      <Skeleton className="size-9" />
    </div>
  );
}

/** 工单详情专用加载态：与正文、活动和属性栏的最终栅格保持一致。 */
export function TicketDetailSkeleton() {
  const { t } = useI18n();

  return (
    <div aria-busy="true" aria-label={t("tickets.detail.loadingDetail")} className="h-full" role="status">
      <span className="sr-only">{t("tickets.detail.loadingDetail")}</span>
      <div aria-hidden="true" className="mx-auto grid min-h-full w-full items-start gap-4 px-2 py-3 lg:h-full lg:min-h-0 lg:grid-cols-3 lg:items-stretch">
        <div className="flex min-h-full flex-col overflow-hidden lg:col-span-2 lg:h-full lg:min-h-0">
          <div className="space-y-3 px-4 pb-2">
            <Skeleton className="h-6 w-3/5" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-4 w-3/5" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-14 rounded-full" />
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-5 w-12 rounded-full" />
            </div>
          </div>

          <div className="flex h-11 shrink-0 items-center gap-4 px-4">
            <Skeleton className="h-7 w-20" />
            <Skeleton className="h-7 w-16" />
          </div>

          <div className="min-h-0 flex-1 space-y-6 overflow-hidden px-4 py-5">
            {ACTIVITY_ROWS.map((width, index) => (
              <div className="flex items-start gap-3" key={width}>
                <Skeleton className="size-8 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1 space-y-2 pt-1">
                  <div className="flex items-center gap-2">
                    <Skeleton className={cn("h-3", width)} />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <Skeleton className={cn("h-4", index % 2 === 0 ? "w-11/12" : "w-3/4")} />
                  {index < 2 ? <Skeleton className="h-4 w-2/3" /> : null}
                </div>
              </div>
            ))}
          </div>

          <div className="shrink-0 px-4 pb-3">
            <Skeleton className="h-28 w-full rounded-lg" />
          </div>
        </div>

        <aside className="hidden h-full min-h-0 overflow-hidden px-4 py-1 lg:block">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Skeleton className="h-3 w-14" />
              <div className="flex items-center gap-2">
                <Skeleton className="size-8 rounded-full" />
                <Skeleton className="h-4 w-28" />
              </div>
            </div>

            {PROPERTY_ROWS.map((row) => (
              <div className="space-y-1.5" key={row.id}>
                <Skeleton className={cn("h-3", row.label)} />
                <Skeleton className={cn("h-4", row.value)} />
              </div>
            ))}

            <div className="space-y-1.5">
              <Skeleton className="h-3 w-12" />
              <div className="flex -space-x-2">
                <Skeleton className="size-8 rounded-full ring-2 ring-background" />
                <Skeleton className="size-8 rounded-full ring-2 ring-background" />
                <Skeleton className="size-8 rounded-full ring-2 ring-background" />
              </div>
            </div>

            <div className="space-y-2">
              <Skeleton className="h-3 w-10" />
              <div className="flex items-center justify-between gap-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <Skeleton className="h-3 w-24" />
            </div>

            <div className="space-y-2">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-3 w-3/5" />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
